<template>
  <div
    v-if="show"
    class="jps-image-selection"
    :style="style"
  >
    <component
      :is="Component"
      :appearance="appearance"
      v-model="loadingState"
      class="size-full"
    >
      <BaseImage
        v-if="!hasCrop || isImageLoadError"
        :src="imgParam?.src"
        :w="w"
        :h="h"
        :natural="natural"
        :cover="cover"
        :background="background"
        :error-alt="_noimageSrc"
        class="size-full"
        @stateChanged="(state) => loadingState = state"
      />
      <template v-else>
        <canvas class="size-full" ref="canvas" :style="canvasStyle" />
      </template>
    </component>

    <div class="jps-image-selection-info" v-if="info">
      <div class="jps-image-selection-title">
        {{ ls(title) }} / {{ ls(attribution) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseThumbnail from '~/components/base/BaseThumbnail.vue'
import type { ThumbnailAppearance } from '~/components/base/BaseThumbnail.vue'

const { ls } = useI18n()
const { contentImageUrl, contentThumbnailUrl, iiifImageUrlFromContent } = useContent()
const { compImageSelection } = useImageSelection()
const { iiifImageUrlFromInfo } = useIIIF()
const { dataTypeNoImage } = useNoImage()

interface Props {
  image?: ImageSelection
  w?: number
  h?: number
  background?: string
  info?: boolean
  hidenoimg?: boolean
  natural?: boolean
  fullsize?: boolean
  type?: string
  coverOverride?: boolean
  noimageSrc?: string
  appearance?: ThumbnailAppearance
  imageUsage?: 'content' | 'thumbnail'
}

const props = withDefaults(defineProps<Props>(), {
  background: null,
  info: false,
  hidenoimg: false,
  natural: false,
  fullsize: false,
  type: 'curation',
  coverOverride: null,
  noimageSrc: null,
})

const internalLoadingState = ref(0)
const loadingState = computed({
  get: () => {
    return hasCrop.value ? 1 : internalLoadingState.value
  },
  set: (newValue) => {
    internalLoadingState.value = newValue
  }
})

const Component = computed(() => {
  return props.imageUsage === 'thumbnail'
    ? BaseThumbnail
    : 'div'
})

watch(
  () => props.image,
  () => nextTick(() => drawImage()),
  { deep: true },
)

onMounted(() => {
  nextTick(() => drawImage())
})

const canvasStyle = computed(() => {
  return {
    'object-fit': cover.value ? 'cover' : ('contain' as any),
  }
})

const canvas = ref<HTMLCanvasElement | null>()

const isImageLoadError: Ref<boolean> = ref(false)

function drawImage() {
  isImageLoadError.value = false
  if (!import.meta.client) return
  if (!hasCrop.value) return
  if (!canvas.value) return
  const ctx = canvas.value.getContext('2d')
  if (!ctx) return

  const img = new Image()
  img.onload = () => {
    const { crop } = imgParam.value
    const width = crop ? crop.w : img.naturalWidth
    const height = crop ? crop.h : img.naturalHeight
    if (canvas.value) {
      canvas.value.width = width
      canvas.value.height = height
    }

    if (crop) {
      ctx.drawImage(
        img,
        crop.x,
        crop.y,
        crop.w,
        crop.h, // source rectangle
        0,
        0,
        width,
        height, // destination rectangle
      )
    } else {
      ctx.drawImage(
        img,
        0,
        0,
        width,
        height,
      )
    }
  }
  img.onerror = () => {
    isImageLoadError.value = true
  }
  img.src = imgParam.value.src
}

const style = computed(() => {
  if (props.w) {
    return { width: `${props.w}px`, height: `${props.h}px` }
  } else {
    return null
  }
})

const completeImageSelection = computed(() => {
  return compImageSelection(props.image)
})

const imgParam = computed(() => {
  if (!props.image || !props.image.imgType) {
    return { src: null, crop: null, cover: false }
  }
  if (props.image.imgType === 'ITEM') {
    //IIIF対応アイテムの場合
    if (props.image.infoJsonUrl) {
      return {
        src: iiifImageUrlFromInfo(
          props.image.infoJsonUrl,
          props.image,
          iiifSize.value,
        ),
        crop: null,
        cover: cover.value,
      }
    } else {
      //通常のサムネイルの場合
      //image.urlに拡張子があって、画像でない値が入っていた場合、サムネイルにフォールバックしようとする
      if (
        props.image.url &&
        props.image.url.match(/\..+$/) &&
        !props.image.url.endsWith('.jpg') &&
        !props.image.url.endsWith('.png') &&
        !props.image.url.endsWith('.gif') &&
        !props.image.url.endsWith('.webp') &&
        props.image.thumbnailUrl
      )
        props.image.url = props.image.thumbnailUrl
      if (hasCrop.value) {
        return {
          src: props.image.url,
          crop: imageCrop.value,
          cover: cover.value,
        }
      } else {
        if (props.fullsize) {
          return {
            src: props.image.url,
            crop: null,
            cover: cover.value,
          }
        } else {
          return {
            src: props.image.thumbnailUrl || props.image.url,
            crop: null,
            cover: cover.value,
          }
        }
      }
    }
  } else if (props.image.imgType === 'URL') {
    if (hasCrop.value) {
      return {
        src: props.image.url,
        crop: imageCrop.value,
        cover: cover.value,
      }
    } else {
      if (props.fullsize) {
        return {
          src: props.image.url,
          crop: null,
          cover: cover.value,
        }
      } else {
        return {
          src: props.image.thumbnailUrl || props.image.url,
          crop: null,
          cover: cover.value,
        }
      }
    }
  } else {
    if (props.image.imgType === 'UPLOAD') {
      if (hasCrop.value) {
        //cropあり
        return {
          src: iiifImageUrlFromContent(
            props.image.contentId,
            props.image.imageId,
            props.image,
            iiifSize.value,
          ),
          crop: null,
          cover: cover.value,
        }
      } else {
        if (props.fullsize) {
          return {
            src: contentImageUrl(props.image.contentId, props.image.imageId),
            crop: null,
            cover: cover.value,
          }
        } else {
          return {
            src: contentThumbnailUrl(
              props.image.contentId,
              props.image.imageId,
            ),
            crop: null,
            cover: cover.value,
          }
        }
      }
    } else if (props.image.imgType === 'IIIF') {
      return {
        src: iiifImageUrlFromInfo(
          props.image.infoJsonUrl,
          props.image,
          iiifSize.value,
        ),
        crop: null,
        cover: cover.value,
      }
    }
  }
  //全て失敗したときのFallBack
  return {
    src: _noimageSrc.value,
    crop: null,
    cover: false,
  }
})

const hasCrop = computed(() => {
  return (
    props.image &&
    props.image.w &&
    props.image.h &&
    props.image.w > 0 &&
    props.image.h > 0
  )
})

const cover = computed(() => {
  return props.coverOverride != null ? props.coverOverride : props.image?.cover
})

const iiifSize = computed(() => {
  if (props.fullsize) {
    return {
      w: 1920,
    }
  } else {
    return {
      w: 640,
    }
  }
})

const imageCrop = computed(() => {
  if (props.image?.w)
    return {
      w: props.image.w,
      h: props.image.h,
      x: props.image.x,
      y: props.image.y,
    }
  else return null
})

const _noimageSrc = computed(() => {
  if (props.noimageSrc) {
    return props.noimageSrc
  } else {
    return dataTypeNoImage(props.type)
  }
})

const isEmpty = computed(() => {
  return (
    !props.image ||
    ((props.image.imgType === 'URL' || props.image.imgType == 'ITEM') &&
      !props.image.url)
  )
})

const show = computed(() => {
  return !isEmpty.value || !props.hidenoimg
})

const title = computed(() => {
  if (!props.image) return { ja: '' }
  return completeImageSelection.value.title
})

const attribution = computed(() => {
  if (!props.image) return { ja: '' }
  return completeImageSelection.value.source
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-image-selection {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;

  .size-full {
    width: 100%;
    height: 100%;
  }

  .jps-image-selection-info {
    position: absolute;
    right: 0px;
    bottom: 0px;
    background: rgba(255, 255, 255, 0.8);
    padding: 0.2rem;

    &:hover {
      background: rgba(255, 255, 255, 0.95);
      transition: 0.15s ease-in-out;
    }

    .jps-image-selection-title {
      font-size: 0.8rem;
      padding: 0.25rem;
      line-height: 1rem;

      @include is-mobile() {
        line-height: 1.2rem;
        font-size: 0.7rem;
        text-align: right;
      }
    }
  }
}
</style>
